/* eslint-disable no-console */
import { ReactElement, useEffect, useState } from 'react';
import { Flex, Box } from 'reflexbox';
import { useTranslation } from 'react-i18next';

import * as graphql from 'generated/graphql';
import { UserSelector } from 'packages/kakapo-ui-kit/components/users/UserCollaboratorsSelector';
import { SubSectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import Column from 'packages/kakapo-ui-kit/components/grid/Column';
import PersonAvatar from 'packages/kakapo-ui-kit/components/users/PersonAvatar';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import DeleteButton from 'packages/kakapo-ui-kit/components/editors/DeleteButton';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';

export interface IProps {
	group: { id: string };
}

export default function MembersTable({ group }: IProps): ReactElement {
	const { t } = useTranslation();
	const authenticatedUser = useAuthenticatedUser();

	const { data, loading, refetch } = graphql.useGroupMembersQuery({
		variables: {
			id: group.id,
		},
	});

	const [optimisticMembers, setOptimisticMembers] = useState<graphql.GroupMemberEdge[]>([]);
	const [loadingUserID, setLoadingUserID] = useState<string | undefined>(undefined);

	useEffect(() => {
		setOptimisticMembers((data?.group.members.edges as graphql.GroupMemberEdge[]) ?? []);
	}, [data]);

	const { data: rolesData } = graphql.useRolesQuery({
		variables: {
			target: graphql.RoleTargetType.Team,
		},
	});

	const [assignRole] = graphql.useAssignRoleMutation();

	const addUser = async (user: graphql.User) => {
		setOptimisticMembers([
			...optimisticMembers,
			{
				node: {
					id: user.id,
					firstName: user.firstName,
					lastName: user.lastName,
				},
				role: {
					id: 'group-member',
					type: graphql.RoleType.Member,
				},
			} as graphql.GroupMemberEdge,
		]);

		updateUser(user.id!, 'group-member');
	};

	const updateUser = async (id: string, roleID: string) => {
		setLoadingUserID(id);

		await assignRole({
			variables: {
				input: {
					userID: id,
					roleID,
					targetType: graphql.RoleTargetType.Team,
					targetID: group.id,
					isIndirect: false,
				},
			},
		});

		await refetch();

		setLoadingUserID(undefined);
	};

	const [revokeRole] = graphql.useRevokeRoleMutation();

	const deleteUser = async (id: string) => {
		setLoadingUserID(id);

		await revokeRole({
			variables: {
				input: {
					userID: id,
					targetType: graphql.RoleTargetType.Team,
					targetID: group.id,
					roleID: 'group-member',
				},
			},
		});

		await refetch();

		setLoadingUserID(undefined);
	};

	const isSuperUser = useViewerRoles().isWorkspaceSuperuser;

	if (loading) {
		return <Spinner isLoading />;
	}

	const ownerID = data?.group.members.edges.find(
		(edge) => edge.node.id === authenticatedUser.id && edge.role.type === graphql.RoleType.Owner,
	)?.node.id;

	const isOwner = authenticatedUser.id === ownerID || isSuperUser;

	return (
		<>
			<Flex alignItems="center">
				<Box mr="10px">
					<SubSectionTitle>{t('common.settings.teams.create.members')}</SubSectionTitle>
				</Box>
			</Flex>

			<Flex mt={3}>
				<HorizontalRule color="lightBlue" />
			</Flex>

			<Flex flexDirection="column">
				{optimisticMembers
					.map(
						(edge) =>
							[
								edge,
								`${edge.node.id === ownerID ? '0' : '1'}-${edge.node.firstName} ${
									edge.node.lastName
								}`,
							] as [graphql.GroupMemberEdge, string],
					)
					.sort((a, b) => a[1].localeCompare(b[1]))
					.map(([edge]) => {
						const fullName = `${edge.node.firstName} ${edge.node.lastName}`;

						return (
							<Flex key={edge.node.id} flexDirection="column">
								<Column py={2} px={3}>
									<Flex flexDirection="row" justifyContent="space-between">
										<Flex flexDirection="row" alignSelf="center">
											<Box mr={3}>
												<PersonAvatar person={edge.node} name={fullName} />
											</Box>
											<SubSectionTitle>{fullName}</SubSectionTitle>
										</Flex>
										<Box alignSelf="center" mx={5} flex={2} />
										<Box
											alignSelf="center"
											ml={2}
											flex="180px"
											flexShrink={0}
											flexGrow={0}
											width={120}
										>
											{isOwner && ownerID !== edge.node.id ? (
												<Select
													key={edge.role.id}
													isDisabled={!!loadingUserID}
													small
													unspaced
													defaultValue={{
														value: edge.role.id,
														label: t(`roles.${edge.role.id}`),
													}}
													options={rolesData?.roles.map((role) => ({
														value: role.id,
														label: t(`roles.${role.id}`),
													}))}
													onChange={(option) => updateUser(edge.node.id, option!.value)}
												/>
											) : (
												t(`roles.${edge.role.id}`)
											)}
										</Box>
										<Box alignSelf="center" ml={4} width={28}>
											{isOwner &&
												ownerID !== edge.node.id &&
												(loadingUserID === edge.node.id ? (
													<Spinner size="18px" isLoading verticalMargin={0} />
												) : (
													!loadingUserID && (
														<DeleteButton onClick={() => deleteUser(edge.node.id)} />
													)
												))}
										</Box>
									</Flex>
								</Column>

								<HorizontalRule color="lightBlue" />
							</Flex>
						);
					})}

				{isOwner && (
					<Box p={3}>
						<UserSelector
							selectedUsers={optimisticMembers.map((edge) => edge.node) as any}
							onSelectUser={(user) => addUser(user!)}
							small
						/>
					</Box>
				)}
			</Flex>
		</>
	);
}
